import { patchVueInstance } from "../../lib/utils/vue";
import { fromJsonData } from '@tencent/data-schema-core/lib/convertor/jsonData';
import { newFunction } from "../../lib/utils/script";
function mergeFunction(a, b) {
  if (!b) return a;
  if (!a) return b;
  return function (...args) {
    a.apply(this, args);
    b.apply(this, args);
  };
}

/**
 * 虽然 UICore 直接拖拽、渲染出来的页面，是具备数据绑定的逻辑的，但是我们总会有需要自定义一些特殊逻辑的场景。
 *
 * 此时可以使用 LessCode 的能力，让用户可以编写一段类似 Vue 的逻辑代码，这段代码会注入到渲染出来的页面中……
 *
 * @see http://w-ui-core.pages.oa.com/exts/lessCode.html
 */
export default class LessCodePlugin {
  /**
   * 用于将 pagelet 里存储的信息，转换为实际的 Vue.ComponentOptions 对象。
   *
   * 默认的做法是，取 `pagelet.lessCode` 这个字符串，替换掉 `export default ` 后直接 eval 一下
   *
   * @param {UICore.Pagelet} pagelet
   * @returns {null | Vue.ComponentOptions<Vue>}
   */
  static defaultDeflateMethod(pagelet) {
    if (!pagelet.lessCode) return null;
    try {
      const fn = newFunction([], ['var __deflated_exports__;(function(){', pagelet.lessCode.replace(/\bexport default\b/, ';__deflated_exports__='), '})();return __deflated_exports__;'].join('\n'));
      return fn();
    } catch (err) {
      console.error('[LessCode] Cannot compile ', pagelet);
      console.error(err);
      return null;
    }
  }
  constructor({
    deflate = LessCodePlugin.defaultDeflateMethod,
    disableLifeCycleCallbacks = 'only-design-mode' // 也可以是 boolean
  } = {}) {
    this.deflate = deflate;
    this.disableLifeCycleCallbacks = disableLifeCycleCallbacks;
  }
  install(uc) {
    uc.hooks.pageletRendererBeforeCreate.tap('LessCode', this.applyToInstance.bind(this));
  }

  /**
   * 改造一个 CRenderer 实例
   *
   * @param {UICore.CRenderer} renderer
   * @param {UICore.Pagelet} pagelet
   */
  applyToInstance(renderer, pagelet) {
    /** @type {UICore.CRenderer} */
    const {
      uc
    } = renderer.$options.propsData; // 此时没有 ucPagelet 的，因为它是 computed

    let deflated = this.deflate(pagelet);
    if (!deflated) return;

    // 这里存放的是在 lessCode 里声明的 data 和 computed 的 key，他们会作为一堆 dataSource 要注册到 CRenderer
    const sourceIdsFromLessCode = [...Object.keys(deflated.computed || {})];

    // 如果定义了 data，那么对那个方法做一点改造，以便更新 sourceIdsFromLessCode 的内容
    if (deflated.data) {
      const oldData = deflated.data;
      deflated.data = function mergedDataFunction(...args) {
        const prevAns = typeof oldData === 'function' ? oldData.apply(this, args) : oldData;
        if (prevAns) sourceIdsFromLessCode.unshift(...Object.keys(prevAns));
        return prevAns;
      };
    }

    // 为了避免安全和逻辑问题，设计模式下只需要 computed 和 data 就可以了
    let {
      disableLifeCycleCallbacks
    } = this;
    if (disableLifeCycleCallbacks === 'only-design-mode') disableLifeCycleCallbacks = uc.isDesignMode;
    if (disableLifeCycleCallbacks) {
      const picked = {};
      ['data', 'computed', 'methods', 'inject', 'provide'].forEach(key => {
        if (key in deflated) picked[key] = deflated[key];
      });
      deflated = picked;
    }

    // 修改 created 钩子，在页面创建了之后，需对应去注册数据源
    deflated.created = mergeFunction(deflated.created, () => {
      sourceIdsFromLessCode.forEach(id => {
        try {
          const data = renderer[id];
          const schema = fromJsonData(data);
          renderer.addDataSourceDeclaration({
            id,
            schema
          });
        } catch (err) {
          console.warn(`[UICore] Cannot register dataSource from lessCode: ${id}`, err);
        }
      });
    });

    // 然后就是搞事
    patchVueInstance(renderer, deflated);

    // 设计模式下，如果用户编辑了 LessCode，需要把 Canvas 区域销毁重建下
    if (uc.isDesignMode) {
      renderer.$on('hook:mounted', () => {
        renderer.$watch(() => renderer.ucPagelet && renderer.ucPagelet.lessCode, () => {
          uc.designer.recreateCanvas();
        });
      });
    }
  }
}